export type ZoneId = 'page1-left' | 'page1-right' | 'page2';

export type SectionKey =
  | 'title'
  | 'subtitle'
  | 'at_a_glance'
  | 'key_metrics'
  | 'testimonial'
  | 'challenges'
  | 'objectives'
  | 'outcomes'
  | 'partnership_details'
  | 'why_choose_riscpoint'
  | 'call_to_action';

export interface SectionCard {
  key: SectionKey;
  visible: boolean;
  order: number;
  size: 'small' | 'medium' | 'large';
}

export interface SectionSpacing {
  marginTop: number;
  marginBottom: number;
}

export interface ZoneDefinition {
  id: ZoneId;
  label: string;
  maxCards: number;
  columns: 1 | 2;
}

export const ZONE_DEFINITIONS: Record<ZoneId, ZoneDefinition> = {
  'page1-left': {
    id: 'page1-left',
    label: 'Page 1 — Left',
    maxCards: 4,
    columns: 1,
  },
  'page1-right': {
    id: 'page1-right',
    label: 'Page 1 — Right',
    maxCards: 3,
    columns: 1,
  },
  'page2': {
    id: 'page2',
    label: 'Page 2',
    maxCards: 3,
    columns: 1,
  },
};

export interface EditorLayout {
  zones: Record<ZoneId, SectionKey[]>;
  sectionSizes: Record<SectionKey, SectionCard['size']>;
  sectionSpacing: Record<SectionKey, SectionSpacing>;
}

export const ALL_SECTION_KEYS: SectionKey[] = [
  'title', 'subtitle', 'at_a_glance', 'key_metrics', 'testimonial',
  'challenges', 'objectives', 'outcomes',
  'partnership_details', 'why_choose_riscpoint', 'call_to_action',
]

const DEFAULT_SECTION_SPACING: Record<SectionKey, SectionSpacing> = Object.fromEntries(
  ALL_SECTION_KEYS.map(k => [k, { marginTop: 0, marginBottom: 0 }])
) as Record<SectionKey, SectionSpacing>

export const DEFAULT_LAYOUT: EditorLayout = {
  zones: {
    'page1-left': ['title', 'subtitle', 'at_a_glance', 'key_metrics', 'testimonial'],
    'page1-right': ['challenges', 'objectives', 'outcomes'],
    'page2': ['partnership_details', 'why_choose_riscpoint', 'call_to_action'],
  },
  sectionSizes: {
    title: 'medium',
    subtitle: 'medium',
    at_a_glance: 'medium',
    key_metrics: 'medium',
    testimonial: 'medium',
    challenges: 'medium',
    objectives: 'medium',
    outcomes: 'medium',
    partnership_details: 'medium',
    why_choose_riscpoint: 'medium',
    call_to_action: 'medium',
  },
  sectionSpacing: DEFAULT_SECTION_SPACING,
};

export const SECTION_LABELS: Record<SectionKey, string> = {
  title: 'Title',
  subtitle: 'Subtitle',
  at_a_glance: 'At a Glance',
  key_metrics: 'Key Metrics',
  testimonial: 'Client Testimonial',
  challenges: 'Challenges',
  objectives: 'Objectives',
  outcomes: 'Outcomes',
  partnership_details: 'Partnership Details',
  why_choose_riscpoint: 'Why Choose RISCPoint',
  call_to_action: 'Call to Action',
};

export interface SubElement {
  key: string;
  label: string;
  baseline: number;
  maxRem: number;
}

export const SECTION_SUB_ELEMENTS: Record<SectionKey, SubElement[]> = {
  title: [{ key: 'title', label: 'Title', baseline: 2.2, maxRem: 3.0 }],
  subtitle: [{ key: 'subtitle', label: 'Subtitle', baseline: 1.2, maxRem: 3.0 }],
  at_a_glance: [{ key: 'at_glance', label: 'Text', baseline: 0.75, maxRem: 1.5 }],
  key_metrics: [
    { key: 'key_metrics_title', label: 'Title', baseline: 0.9, maxRem: 1.5 },
    { key: 'key_metrics_description', label: 'Description', baseline: 0.75, maxRem: 1.5 },
  ],
  testimonial: [{ key: 'testimonial', label: 'Text', baseline: 0.75, maxRem: 1.5 }],
  challenges: [{ key: 'challenges', label: 'Text', baseline: 0.75, maxRem: 1.5 }],
  objectives: [{ key: 'objectives', label: 'Text', baseline: 0.75, maxRem: 1.5 }],
  outcomes: [{ key: 'outcomes', label: 'Text', baseline: 0.9, maxRem: 1.5 }],
  partnership_details: [
    { key: 'partnership_client_overview', label: 'Client Overview', baseline: 0.8, maxRem: 1.5 },
    { key: 'partnership_bullets', label: 'Details', baseline: 0.7, maxRem: 1.5 },
  ],
  why_choose_riscpoint: [{ key: 'why_choose_riscpoint', label: 'Text', baseline: 0.75, maxRem: 1.5 }],
  call_to_action: [{ key: 'call_to_action', label: 'Text', baseline: 0.8, maxRem: 1.5 }],
};
